import { mergeRefs } from '@mntn-dev/utilities'
import { type KeyboardEvent, type RefAttributes, useRef } from 'react'
import { IconButton } from '../buttons/icon-button.tsx'
import { Icon } from '../icon/icon.tsx'
import { useSelectContext } from './use-select.ts'

type SelectSearchProps = RefAttributes<HTMLInputElement>

export const SelectSearch = <ValueType extends string>({
  ref,
}: SelectSearchProps) => {
  const searchRef = useRef<HTMLInputElement>(null)
  const mergeRef = mergeRefs(ref, searchRef)
  const {
    onSearch,
    search,
    searchPlaceholder = 'Search',
    searchable,
  } = useSelectContext<ValueType>()

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()

      onSearch(e.currentTarget.value)
    }
  }

  const handleClear = () => {
    if (searchRef.current) {
      searchRef.current.value = ''
    }

    onSearch('')
  }

  if (!searchable) {
    return null
  }

  return (
    <div className="flex-initial relative h-full w-full">
      <input
        type="text"
        placeholder={searchPlaceholder}
        ref={mergeRef}
        onKeyDown={handleKeyDown}
        className="text-sm bg-container text-primary placeholder:text-placeholder p-3 pr-11 leading-6 w-full border-none focus-visible:outline-none"
      />
      {search === '' ? (
        <Icon
          name="MagnifyingGlassIcon"
          size="md"
          color="info"
          className="absolute right-3 top-1/2 transform -translate-y-1/2"
        />
      ) : (
        <IconButton
          name="XMarkIcon"
          size="md"
          color="info"
          className="absolute right-3 top-1/2 transform -translate-y-1/2"
          onClick={handleClear}
        />
      )}
    </div>
  )
}
